import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; // Import hooks
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import '../Pages/Home.css';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Where the "My Account" link should point based on auth state / role
  const accountPath = user
    ? (user.role === 'admin' ? '/admin' : '/dashboard')
    : '/login';

  // State for controlling dropdowns
  const [isEventsOpen, setIsEventsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  // State for mobile navbar
  const [isNavOpen, setIsNavOpen] = useState(false);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const closeNav = () => {
    setIsNavOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false); // Close dropdown after selection
  };

  const toggleEvents = (e) => {
    e.preventDefault();
    setIsEventsOpen(!isEventsOpen);
    setIsLangOpen(false); // Close other dropdown
  };

  const toggleLang = (e) => {
    e.preventDefault();
    setIsLangOpen(!isLangOpen);
    setIsEventsOpen(false); // Close other dropdown
  };

  // Custom Smooth Scroll Function
  const smoothScroll = (e, targetId) => {
    // 1. Check if we are on the Home Page
    if (location.pathname === '/') {
      e.preventDefault(); // Only prevent default on Home Page to stop instant jump
      setIsNavOpen(false); // Close mobile menu

      const target = targetId ? document.getElementById(targetId) : document.body; // scroll to body/top if no ID

      if (target) {
        const targetPosition = target.getBoundingClientRect().top + window.scrollY;
        const startPosition = window.scrollY;
        const distance = targetPosition - startPosition;
        const duration = 1200; // 1.2 seconds
        let start = null;

        window.requestAnimationFrame(step);

        function step(timestamp) {
          if (!start) start = timestamp;
          const progress = timestamp - start;

          // Cubic Ease-In-Out
          let ease = progress / duration;
          ease = ease < 0.5
            ? 4 * ease * ease * ease
            : 1 - Math.pow(-2 * ease + 2, 3) / 2;

          window.scrollTo(0, startPosition + distance * ease);

          if (progress < duration) {
            window.requestAnimationFrame(step);
          }
        }
      }
    } else {
      // 2. We are NOT on Home Page
      // Do NOT prevent default. Let Link navigate.
      // For sections (About/Blog), we need to ensure they navigate to Home first.

      // If targetId is provided, we might want to navigate to home with hash
      // But standard Link to="/" works for Home.
      // For About, we should use a Link to="/#about" ideally, 
      // but simplistic approach: just navigate to Home.
      if (targetId) {
        // If we want to support jumping to section from other pages, 
        // we'd typically use <Link to="/#about">.
        // For now, let's allow the Link component to handle the URL change.
      }
    }
  };

  return (
    <nav
      className="navbar navbar-expand-lg shadow-sm fixed-top"
      // dir="rtl" // Removed hardcoded RTL
      style={{
        paddingTop: "0",
        paddingBottom: "0",
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        backdropFilter: "blur(7px)",
        WebkitBackdropFilter: "blur(10px)",
        zIndex: 1000,
      }}
    >
      <div className="container">
        {/* LOGO */}
        <Link
          className="navbar-brand d-flex align-items-center"
          to="/"
          onClick={(e) => smoothScroll(e, null)} // Scroll to top
        >
          <img
            src="/img/logo.png"
            alt="Logo"
            width="65"
            height="70"
            className="d-inline-block align-text-top"
            style={{
              marginTop: "0",
              marginBottom: "0",
            }}
          />
        </Link>

        {/* MOBILE TOGGLE */}
        <button
          className="navbar-toggler"
          type="button"
          onClick={toggleNav}
          aria-controls="navbarScroll"
          aria-expanded={isNavOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`} id="navbarScroll">
          <ul
            className={`navbar-nav ${i18n.language === 'ar' ? 'ms-auto' : 'me-auto'} my-2 my-lg-0 navbar-nav-scroll`}
            style={{ "--bs-scroll-height": "250px" }}
          >
            {/* HOME */}
            <li className="nav-item active">
              <Link
                className="nav-link active"
                to="/"
                onClick={(e) => smoothScroll(e, null)} // Scroll to top
              >
                {t('Navbar.Home')}
              </Link>
            </li>


            {/* ABOUT */}
            <li className="nav-item active">
              <Link
                className="nav-link active"
                to="/#about" // Changed to Link with hash path
                onClick={(e) => smoothScroll(e, "about")}
              >
                {t('Navbar.About')}
              </Link>
            </li>



            {/* DROPDOWN: EVENTS */}
            <li className={`nav-item dropdown ${isEventsOpen ? 'show' : ''}`}>
              <a
                className="nav-link active dropdown-toggle"
                href="#"
                id="navbarScrollingDropdown"
                role="button"
                aria-expanded={isEventsOpen}
                onClick={toggleEvents}
              >
                {t('Navbar.Events')}
              </a>
              <ul
                className={`dropdown-menu text-end ${isEventsOpen ? 'show' : ''}`}
                aria-labelledby="navbarScrollingDropdown"
              >
                <li>
                  <Link className="dropdown-item" to="/SecondaryEngipi" onClick={() => { setIsEventsOpen(false); closeNav(); }}>
                    {t('Navbar.SecondaryCommunity')}
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/Page360" onClick={() => { setIsEventsOpen(false); closeNav(); }}>
                    {t('Navbar.Community360')}
                  </Link>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>

              </ul>
            </li>

            {/* LINKS */}
            <li className="nav-item">
              <Link
                className="nav-link active"
                to="/#blog-carousel" // Changed to Link with hash path
                onClick={(e) => smoothScroll(e, "blog-carousel")}
              >
                {t('Navbar.Blog')}
              </Link>
            </li>
            <li className="nav-item">
              <a
                className="nav-link active"
                href="https://wa.me/905076526051"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Navbar.Contact')}
              </a>
            </li>

            {/* MY ACCOUNT */}
            <li className="nav-item">
              <Link
                className="nav-link active"
                to={accountPath}
                onClick={closeNav}
              >
                <i className={`bi bi-person-circle ${i18n.language === 'ar' ? 'ms-1' : 'me-1'}`} />
                {t('Navbar.MyAccount')}
              </Link>
            </li>

            <a
              href="https://forms.gle/6J7PGQL2xB8CanQd6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-warning"
            >
              {t('Navbar.JoinUs')}
            </a>

          </ul>

          {/* SEARCH */}
          <form className="d-flex me-2">
            <input
              className="form-control ms-2"
              type="search"
              placeholder={t('Navbar.SearchPlaceholder')}
              aria-label="Search"
              style={{ textAlign: i18n.language === 'ar' ? "right" : "left" }}
            />
            <button type="button" className="btn btn-outline-warning">{t('Navbar.SearchButton')}</button>
          </form>

          {/* Language Switcher */}
          <div className="d-flex align-items-center ms-3">
            <div className={`dropdown ${isLangOpen ? 'show' : ''}`}>
              <button
                className="btn btn-light dropdown-toggle"
                type="button"
                id="languageDropdown"
                aria-expanded={isLangOpen}
                onClick={toggleLang}
              >
                <i className="bi bi-globe"></i> {i18n.language.toUpperCase()}
              </button>
              <ul className={`dropdown-menu ${isLangOpen ? 'show' : ''}`} aria-labelledby="languageDropdown">
                <li><button className="dropdown-item" onClick={() => changeLanguage('ar')}>العربية (AR)</button></li>
                <li><button className="dropdown-item" onClick={() => changeLanguage('en')}>English (EN)</button></li>
                <li><button className="dropdown-item" onClick={() => changeLanguage('tr')}>Türkçe (TR)</button></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
